// One plain category per AI claim request (Claims_Agent__c), so every screen says WHY a request ended (Cesar 2026-10-09: "Miss: no clear
// result hides the useful information; separate online filing held, blocked before dialing, voicemail, staff handoff, and unknown outcome").
// Port of sj-insurance-router lib/claims_agent/miss_category.js; test/fixtures/miss_category_cases.json is the shared contract (same file).
// "Calls placed" counts only DIALED requests: a Retell call id in claims_agent_memory, or (when that is unreadable) a reason only a call
// can produce.
export const MISS_CATEGORIES = {
    CLAIM_OPENED: { group: 'DIALED', label: 'Got a claim number' },
    VOICEMAIL: { group: 'DIALED', label: 'Voicemail or no answer' },
    STAFF_HANDOFF: { group: 'DIALED', label: 'Handed to our staff' },
    NOT_FOUND: { group: 'DIALED', label: 'Carrier could not find the policy or insured' },
    CALL_DROPPED: { group: 'DIALED', label: 'Call dropped' },
    CLAIM_CHECK: { group: 'DIALED', label: 'Claim number heard, needs checking' },
    OTHER: { group: 'DIALED', label: 'Other call result' },
    UNKNOWN: { group: 'DIALED', label: 'Called, no result recorded' },
    ONLINE_HELD: { group: 'NOT_DIALED', label: 'State Farm online filing held' },
    BLOCKED: { group: 'NOT_DIALED', label: 'Stopped before dialing' },
    PENDING: { group: 'PENDING', label: 'Waiting for the result' },
    NO_RESULT: { group: 'NOT_DIALED', label: 'Never ran' },
};
const ONLINE_WORDS = {
    GATE_1P_LINK: 'the client is not the policyholder on the State Farm record',
    GATE_1P_COVERAGE_MISSING: 'no coverage is listed on the first-party record',
    GATE_1P_COVERAGE_CONFLICT: 'the coverage on the first-party record conflicts',
    GATE_CASE_TYPE: "the case type is not one State Farm's form takes",
    GATE_SIDE: 'the record is on the wrong side for this filing',
    GATE_CARRIER: 'the carrier account is not a State Farm filing account',
    GATE_CLAIM_EXISTS: 'a claim number already exists',
    GATE_CLAIM_EXISTS_SIBLING: 'another record already has a claim for this policy',
    GATE_TCR_TARGET: 'a third-party filing needs a police report naming the State Farm driver',
    GATE_TCR_TARGET_NOT_AT_FAULT: 'the police report does not put the State Farm driver at fault',
    GATE_TCR_CLIENT_AT_FAULT: 'the police report puts our client at fault',
    GATE_TCR_TARGET_AMBIGUOUS: 'the police report could match two State Farm vehicles',
    GATE_TARGET_MISSING: 'no other driver is on file',
    GATE_TARGET_IS_CLIENT: 'the other driver on file is our client',
    GATE_CLIENT_MISSING: 'the client is missing from the record',
    GATE_HOUSEHOLD: 'the other driver lives with our client',
    TCR_REPORT_INCONSISTENT: 'two police reports disagree',
    SNAPSHOT_UNBUILDABLE: 'the case data could not be read',
    ANSWER_POLICY_NUMBER_MISSING: 'no policy number',
    ANSWER_POLICY_NUMBER_SHAPE: 'the policy number does not fit a State Farm format',
    ANSWER_LOCATION_UNPARSEABLE: 'the crash location could not be read',
    ANSWER_LOCATION_MISSING: 'no crash location',
    ANSWER_SF_VEHICLE_MAKE_MISSING: "the State Farm vehicle's make is missing",
    ANSWER_SF_VEHICLE_ACTION_MISSING: 'what the State Farm car did is not recorded',
    ANSWER_1P_UNMAPPED: 'the first-party form is not mapped yet',
};
export function missCodeOf(missReason) {
    if (!missReason)
        return null;
    const m = /^\s*([A-Za-z_ ]{2,40}?)\s*:/.exec(String(missReason));
    return m ? m[1].trim() : null;
}
function bodyOf(missReason) {
    const i = missReason.indexOf(':');
    return (i >= 0 && i < 45 ? missReason.slice(i + 1) : missReason).replace(/\s+/g, ' ').trim();
}
const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const short = (s, n) => (s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s);
export function onlineWords(rest) {
    const code = String(rest || '').trim().split(/[\s:]/)[0].replace(/^SKIPPED_/, '');
    if (!code)
        return 'a filing check failed';
    return ONLINE_WORDS[code] ?? code.toLowerCase().replace(/^(gate|answer)_/, '').replace(/_/g, ' ');
}
export function categorize(can, opts = {}) {
    const c = can ?? {};
    const disp = String(c.Disposition__c ?? '').trim();
    const reason = String(c.Miss_Reason__c ?? '').trim();
    const code = missCodeOf(reason);
    const body = bodyOf(reason);
    const detail = body && !/^\((no detail returned|compose failed)\)$/i.test(body) ? body : '';
    const out = (key, text) => ({ key, group: MISS_CATEGORIES[key].group, label: MISS_CATEGORIES[key].label, text, dialed: MISS_CATEGORIES[key].group === 'DIALED' });
    if (disp === 'Success')
        return out('CLAIM_OPENED', 'The AI got a claim number');
    if (disp !== 'Miss') {
        if (!blank(c.Claim_Number__c))
            return out('CLAIM_CHECK', 'The AI heard a claim number; it needs checking');
        const limit = opts.pendingMinutes ?? 60;
        const ageMin = c.CreatedDate && opts.nowMs ? (opts.nowMs - Date.parse(c.CreatedDate)) / 60000 : 0;
        if (opts.dialed === true)
            return ageMin > limit ? out('UNKNOWN', 'The AI called but no result was recorded') : out('PENDING', 'The call result is on its way');
        return ageMin > limit ? out('NO_RESULT', 'The request never produced a call or a result') : out('PENDING', 'The call result is on its way');
    }
    if (code === 'ONLINE_UNFILABLE' || code === 'ONLINE_STOPPED')
        return out('ONLINE_HELD', `State Farm online filing held: ${onlineWords(body)}`);
    if (code === 'Not Called')
        return out('BLOCKED', `Not called: ${short(detail || 'a safety check stopped the call', 140)}`);
    if (code === 'UNVERIFIED_CLAIM')
        return out('CLAIM_CHECK', 'The AI heard a claim number; it needs checking');
    if (code === 'VOICEMAIL')
        return out('VOICEMAIL', detail ? `Voicemail: ${short(detail, 140)}` : 'The call reached voicemail');
    if (code === 'TRANSFERRED_OUT')
        return out('STAFF_HANDOFF', detail ? `Handed to our staff: ${short(detail, 140)}` : 'The AI handed the call to our staff');
    if (code === 'POLICY_NOT_FOUND')
        return out('NOT_FOUND', detail ? `Not found: ${short(detail, 140)}` : "The carrier couldn't find the policy or insured");
    if (code === 'CALL_DROPPED' || code === 'ABRUPT_HANGUP') {
        if (/never connected|no answer|busy|voice ?mail/i.test(detail))
            return out('VOICEMAIL', `No answer: ${short(detail, 140)}`);
        return out('CALL_DROPPED', detail ? `Call dropped: ${short(detail, 140)}` : 'The call dropped');
    }
    if (code === 'UNVERIFIED_NO_CLAIM' || code === 'UNKNOWN')
        return out('UNKNOWN', detail ? `Called, no clear result: ${short(detail, 140)}` : 'The AI called but no result was recorded');
    if (!reason)
        return opts.dialed === false ? out('BLOCKED', 'Stopped before dialing (no reason recorded)') : out('UNKNOWN', 'The AI called but no result was recorded');
    return out('OTHER', detail ? short(detail, 160) : 'The call ended without a clear result');
}
//# sourceMappingURL=miss_category.js.map